'use client';

import { faCalendar, faCreditCard, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { faBadgeCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatLocation } from '@pickleballinc/lib/format-location';
import dayjs from 'dayjs';
import { usePathname, useSearchParams } from 'next/navigation';

import { DetailsCard } from '@/components/molecules/DetailsCard';
import { DetailsPageCardsSkeleton } from '@/components/molecules/skeletons/DetailsPageCardsSkeleton';
import { currencyFormats } from '@/entities/shared/lookups/CurrencyFormat';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useCurrenciesQuery } from '@/hooks/lookups/useCurrencyQuery';
import { useSeasonInfoQuery } from '@/hooks/teamLeagueSeasonInfo/useSeasoninfoQuery';
import { publicEnv } from '@/lib/env';
import { useOrganizationStore } from '@/store/organization-store';
import { determinePriceDisplay } from '@/utils/utils';

type Props = { teamLeagueId: string };

export const TeamLeagueDetailsCards = ({ teamLeagueId }: Props) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const isRoot = pathname.split('/').filter(Boolean).length === 2;

	const teamLeagueInfo = useTeamLeagueInfo();

	const { data: seasonInfo, isFetching } = useSeasonInfoQuery(teamLeagueId, searchParams.get('season'));
	const { data: currencies, isFetching: isFetchingCurrencies } = useCurrenciesQuery(Boolean(seasonInfo?.data && teamLeagueInfo));
	const currency = currencies?.data?.find((currency) => currency.id === teamLeagueInfo.currencyId.toString());

	const data = seasonInfo?.data;
	const store = useOrganizationStore();

	const startFormattedDate =
		data?.registrationDateOpen &&
		dayjs(data?.registrationDateOpen)
			.tz(store.teamLeagueTimeZone?.ianaTzIdentifier || 'UTC')
			.format('MMM D YYYY');

	const startFormattedTime =
		data?.registrationDateOpen &&
		dayjs(data?.registrationDateOpen)
			.tz(store.teamLeagueTimeZone?.ianaTzIdentifier || 'UTC')
			.format('h:mm A');

	const endFormattedDate =
		data?.registrationDateClose &&
		dayjs(data?.registrationDateClose)
			.tz(store.teamLeagueTimeZone?.ianaTzIdentifier || 'UTC')
			.format('MMM D YYYY');
	const endFormattedTime =
		data?.registrationDateClose &&
		dayjs(data?.registrationDateClose)
			.tz(store.teamLeagueTimeZone?.ianaTzIdentifier || 'UTC')
			.format('h:mm A');

	// const showLocation = data?.teamLeagueLocation.length === 1;
	const firstLocation = data?.teamLeagueLocation.find(({ isPrimary }) => !!isPrimary) ?? data?.teamLeagueLocation?.[0];

	if (!isRoot) return null;

	const isFetchingData = isFetching || isFetchingCurrencies;

	if (isFetchingData) {
		return <DetailsPageCardsSkeleton />;
	}

	return (
		<div className="flex flex-col">
			{teamLeagueInfo.approvedSanctioningBodies.length !== 0 && (
				<div className="w-full mt-4 rounded-lg">
					<div className="w-full overflow-x-auto bg-white rounded-t-lg scrollbar-thin">
						<div className="flex items-center justify-center gap-4 h-16 px-6 py-2 min-w-max">
							{teamLeagueInfo.approvedSanctioningBodies.map((sanctioningBody) => {
								const sanctioningBodyLogo = sanctioningBody?.logo?.url;
								const sanctioningBodyOrgId = sanctioningBody.organizationUuid;

								const actualLogoURL =
									sanctioningBodyLogo &&
									!sanctioningBodyLogo?.startsWith(publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL) &&
									!sanctioningBodyLogo?.startsWith('https://cdn.pickleball.com') &&
									!sanctioningBodyLogo?.startsWith('https://cdn.pickleballbrackets.com')
										? `https://pickleballbrackets.com/uploads/clubs/${sanctioningBodyOrgId}/${sanctioningBodyLogo}`
										: sanctioningBodyLogo;

								return (
									<div key={sanctioningBody.sanctioningBodyUuid} className="flex items-center gap-2 h-full">
										{!!actualLogoURL && <img src={actualLogoURL} className="h-11" alt="Logo" />}
										<span className="text-base font-semibold leading-6 text-gray-900 w-full text-nowrap">
											{sanctioningBody.organizationName}
										</span>
									</div>
								);
							})}
						</div>
					</div>

					<div className="flex items-center justify-center gap-2 h-9 px-6 py-2 w-full rounded-b-lg bg-gray-200 text-gray-900">
						<FontAwesomeIcon icon={faBadgeCheck} className="size-5 flex-shrink-0" />
						<span className="text-sm font-medium leading-5">Sanctioned</span>
					</div>
				</div>
			)}

			<div className="mb-6 mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
				{!teamLeagueInfo.hideAllRegistrationInfo && (
					<>
						<DetailsCard
							title="When"
							titleIcon={<FontAwesomeIcon icon={faCalendar} />}
							customTitle
							content={
								<div className="flex w-full flex-row gap-2">
									<div className="w-1/2 text-gray-900">
										<div className="mb-4 font-bold">Registration opens</div>
										{data?.registrationDateOpen ? (
											<>
												<div>{startFormattedDate}</div>
												<div className="font-medium text-gray-500">{startFormattedTime}</div>
											</>
										) : (
											<span>TBD</span>
										)}
									</div>
									<div className="w-1/2 text-gray-900">
										<div className="mb-4 font-bold">Registration ends</div>
										{data?.registrationDateClose ? (
											<>
												<div>{endFormattedDate}</div>
												<div className="font-medium text-gray-500">{endFormattedTime}</div>
											</>
										) : (
											<span>TBD</span>
										)}
									</div>
								</div>
							}
						/>
						<DetailsCard
							title="Registration Cost"
							customTitle
							titleIcon={<FontAwesomeIcon icon={faCreditCard} />}
							content={
								<div className="flex w-full flex-col items-center gap-2 ">
									<div className={'text-4xl font-medium text-darkorange-600'}>
										{determinePriceDisplay((seasonInfo?.data?.cost || 0) / 100, currencyFormats[currency?.abbreviation || 'USD'])}
									</div>
								</div>
							}
						/>
					</>
				)}
				{!teamLeagueInfo.hideTlLocation && (
					<DetailsCard
						title="Where"
						titleIcon={<FontAwesomeIcon icon={faLocationDot} />}
						customTitle
						subtitle={
							<div className="flex justify-end">
								<a
									target="_blank"
									href={`https://www.google.com/maps?q=${firstLocation?.venue}, ${firstLocation?.streetAddress}, ${firstLocation?.zip}`}
									aria-label="Link to get directions (opens in a new tab)"
									rel="noreferrer"
								>
									<div className="font-medium text-gray-600 underline">Get Directions</div>
								</a>
							</div>
						}
						content={
							<div className="flex w-full flex-col gap-2 text-gray-900">
								<div className="font-bold">{firstLocation?.venue}</div>
								<div>{firstLocation?.streetAddress}</div>
								<div className="font-medium text-gray-900">
									{formatLocation([firstLocation?.city, firstLocation?.countryInfo?.abbreviation, firstLocation?.zip])}
								</div>
							</div>
						}
					/>
				)}
			</div>
		</div>
	);
};
